/**
 * Phase 5c — org_admin 인 사용자가 자기 조직(들) 의 가입 요청을 승인/거절.
 *
 * 일반 해결책: 회사 관리자가 별도 admin 콘솔로 이동하지 않고 portal 한 곳에서
 * 자기 역할에 맞는 모든 작업 수행. user.orgs 중 role==='org_admin' 인 모든
 * 조직을 자동 모음 (회사별 특수 처리 X).
 */
import { useEffect, useState } from 'react';
import { Check, Inbox, X } from 'lucide-react';
import { toast } from 'sonner';

import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AuthApiError, getAuthClient } from '@/api/client';
import type { JoinRequest } from '@/api/types';
import { errorMessage } from '@/lib/errorMap';
import { useAuthStore } from '@/stores/useAuthStore';

export function OrgApprovalsPage() {
  const user = useAuthStore((s) => s.user);
  const adminOrgs = (user?.orgs ?? []).filter((o) => o.role === 'org_admin');
  const [selectedOrgId, setSelectedOrgId] = useState<string>(adminOrgs[0]?.id ?? '');
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<JoinRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const refresh = async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      const items = await getAuthClient().listJoinRequestsForOrg(selectedOrgId, 'pending');
      setRequests(Array.isArray(items) ? items : []);
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const onApprove = async (id: string) => {
    try {
      await getAuthClient().approveJoinRequest(id);
      toast.success('승인됐습니다');
      void refresh();
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
    }
  };

  const onReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    try {
      await getAuthClient().rejectJoinRequest(rejectTarget.id, rejectReason.trim());
      toast.success('거절됐습니다');
      setRejectTarget(null);
      setRejectReason('');
      void refresh();
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
    }
  };

  if (adminOrgs.length === 0) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl text-sm text-muted-foreground">
          관리자 권한이 있는 조직이 없습니다.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <div className="flex items-center gap-2">
          <Inbox className="size-5" />
          <h1 className="text-2xl font-semibold">가입 요청 승인</h1>
        </div>

        {adminOrgs.length > 1 && (
          <div className="flex items-center gap-2">
            <Label htmlFor="org-select" className="shrink-0">조직</Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger id="org-select" className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {adminOrgs.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              대기 중 ({requests.length}건)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-sm text-muted-foreground">불러오는 중...</div>
            ) : requests.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                대기 중인 가입 요청이 없습니다.
              </div>
            ) : (
              <ul className="flex flex-col divide-y">
                {requests.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-start justify-between gap-3 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">검토 중</Badge>
                        <span className="text-sm font-medium">
                          {r.user_name ?? '(이름 미상)'}
                        </span>
                        {r.user_email && (
                          <span className="text-xs text-muted-foreground">
                            {r.user_email}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          · {r.requested_role === 'org_admin' ? '관리자' : '일반 멤버'} 신청
                        </span>
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-muted-foreground break-all">
                        user_id: {r.user_id}
                      </div>
                      {r.message && (
                        <div className="mt-1 text-sm text-muted-foreground">
                          메시지: {r.message}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-muted-foreground">
                        {new Date(r.requested_at).toLocaleString('ko-KR')}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button size="sm" onClick={() => onApprove(r.id)}>
                        <Check className="size-4" />
                        승인
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRejectTarget(r)}
                      >
                        <X className="size-4" />
                        거절
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={rejectTarget !== null}
        onOpenChange={(o) => !o && (setRejectTarget(null), setRejectReason(''))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>가입 요청 거절</DialogTitle>
            <DialogDescription>
              사유를 입력하세요. 사용자에게 표시됩니다.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="reject-reason">거절 사유</Label>
            <Input
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="예: 소속 증빙 부족"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason('');
              }}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim()}
              onClick={onReject}
            >
              거절
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
